import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Panel, NativeSelect, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LinkRequestDetails } from "@/components/LinkRequestDetails";

function DecisionDialog({ target, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); }, [target]);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/admin/link-requests/${target.req.id}/decision`, { approve: target.approve, note }); toast.success(target.approve ? "Link approved" : "Link rejected"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="link-decision-dialog">
        <DialogHeader><DialogTitle>{target.approve ? "Approve" : "Reject"} link to account {target.req.account_number}</DialogTitle>
          <DialogDescription>{target.approve ? "The consumer will see this account's bills in the mobile app." : "Tell the consumer why the request was rejected."}</DialogDescription></DialogHeader>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={target.approve ? "Note (optional)" : "Reason (required)"} data-testid="link-decision-note" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (!target.approve && note.trim().length < 3)} variant={target.approve ? "default" : "destructive"} data-testid="link-decision-submit">{target.approve ? "Approve" : "Reject"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value }) {
  if (value == null || value === "") return null;
  return <p className="text-xs text-slate-600 break-words"><span className="text-slate-400">{label}: </span>{typeof value === "object" ? JSON.stringify(value) : String(value)}</p>;
}

export default function LinkRequests() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [target, setTarget] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/link-requests", { params: { status } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageHeader eyebrow="Consumers" title="Account-linking requests" description="Consumers ask to link a CASURECO II account number to their mobile app profile." actions={
        <NativeSelect value={status} onChange={setStatus} testId="link-status-filter" className="w-40"
          options={["pending", "approved", "rejected", "removed", "all"].map((s) => ({ value: s, label: s[0].toUpperCase() + s.slice(1) }))} />} />
      {items === null ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? <Empty>No {status === "all" ? "" : status} requests.</Empty> : (
        <div className="grid gap-3">{items.map((r) => (
          <Panel key={r.id} testId={`link-request-${r.id}`}>
            <div className="flex flex-col sm:flex-row justify-between gap-3 min-w-0 cursor-pointer" onClick={() => setDetail(r)}>
              <div className="space-y-1 min-w-0">
                <p className="font-medium text-sm break-words">Account <span className="font-mono">{r.account_number}</span>
                  <span className="text-xs text-slate-500"> · {r.consumer_name || "Unknown consumer"} · {r.consumer_email} · {r.account_type_label}</span></p>
                <Detail label="Reference" value={r.reference} />
                <Detail label="Submitted" value={r.submitted ? fmtDate(r.submitted) : null} />
                <Detail label="Relationship" value={r.relationship} />
                <p className={`text-xs ${r.account_on_file ? "text-emerald-700" : "text-rose-700"}`} data-testid={`link-on-file-${r.id}`}>
                  {r.account_on_file ? `On file: ${r.account_on_file.name} (${r.account_on_file.status})` : "Account number not found in subscriber records"}</p>
                <Detail label="Review note" value={r.review_note} />
              </div>
              <div className="flex items-start gap-2 shrink-0" onClick={(e) => e.stopPropagation()}><StatusBadge status={r.status} />
                {r.status === "pending" && <>
                  <Button size="sm" disabled={!r.account_on_file} title={r.account_on_file ? "" : "Account number not on file"} onClick={() => setTarget({ req: r, approve: true })} data-testid={`link-approve-${r.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => setTarget({ req: r, approve: false })} data-testid={`link-reject-${r.id}`}>Reject</Button></>}
              </div>
            </div>
          </Panel>
        ))}</div>
      )}
      <DecisionDialog target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); setDetail(null); load(); }} />
      {detail && <LinkRequestDetails req={detail} onClose={() => setDetail(null)} onDecide={(approve) => setTarget({ req: detail, approve })} />}
    </div>
  );
}
